// Game Data: Characters, Supplies, Wheel outcomes, Monthly Scenarios, Random Events
// All copy ASH Interactive branded, lightly educational, PG.

import { ASH_MEDIA } from "@/game/media";

export const CHARACTERS = [
  {
    id: "brand_wrangler",
    name: "Brand Wrangler",
    avatar: "/characters/brand_wrangler.png?v=2",
    strengths: ["Trust", "Clarity", "Consistency"],
    weakness: "Slower awareness growth",
    ability: { name: "Clear the Fog", uses: 1, description: "Once per game, turns a confusing marketing decision into a stronger outcome." },
    quote: "Your logo is not your brand, partner.",
    statBoost: { trust: 10, systems: 5 },
    statPenalty: { awareness: -5 },
  },
  {
    id: "content_ranger",
    name: "Content Ranger",
    avatar: "/characters/content_ranger.png?v=2",
    strengths: ["Awareness", "Engagement", "Storytelling"],
    weakness: "Loses energy faster",
    ability: { name: "Batch Content Bonanza", uses: 1, description: "Once per game, creates a large awareness boost without losing energy." },
    quote: "One customer question equals seven pieces of content.",
    statBoost: { awareness: 10, leads: 3 },
    statPenalty: { energy: -5 },
  },
  {
    id: "seo_prospector",
    name: "SEO Prospector",
    avatar: "/characters/seo_prospector.png?v=2",
    strengths: ["Long-term leads", "Website strength", "Data"],
    weakness: "Slower early results",
    ability: { name: "Search Gold", uses: 1, description: "Once per game, reveals one hidden long-term benefit before a decision." },
    quote: "There's gold in them there keywords.",
    statBoost: { systems: 10, leads: 5 },
    statPenalty: { awareness: -5 },
  },
  {
    id: "referral_scout",
    name: "Referral Scout",
    avatar: "/characters/referral_scout.png?v=2",
    strengths: ["Partnerships", "Trust", "Community visibility"],
    weakness: "Less effective without follow-up systems",
    ability: { name: "Warm Lead Wagon", uses: 1, description: "Turns one networking opportunity into extra leads." },
    quote: "Never leave the campfire without a follow-up plan.",
    statBoost: { leads: 10, trust: 5 },
    statPenalty: { systems: -5 },
  },
  {
    id: "scrappy_founder",
    name: "Scrappy Founder",
    avatar: "/characters/scrappy_founder.png?v=2",
    strengths: ["Adaptability", "Budget control", "Recovery"],
    weakness: "Higher risk of scattered marketing",
    ability: { name: "Duct Tape Fix", uses: 1, description: "Once per game, reduces the damage from one bad decision." },
    quote: "It's not pretty, but it's posted.",
    statBoost: { energy: 10, budget: 250 },
    statPenalty: { systems: -5 },
  },
];

export const BUSINESS_TYPES = [
  "Local service business",
  "Professional service business",
  "Retail or e-commerce",
  "Restaurant, venue or hospitality",
  "Nonprofit",
  "Creative or marketing business",
  "Startup product",
  "Other",
];

export const MAIN_GOALS = [
  "More leads",
  "More sales",
  "More bookings",
  "More donations",
  "More awareness",
  "More foot traffic",
  "Better brand credibility",
  "Better marketing consistency",
];

export const WHEEL_OUTCOMES = [
  { id: "bootstrap", label: "Bootstrap Surprise", budget: 750, color: "#EF4444", weight: 1, message: "The trail has chosen chaos. You'll need creativity, consistency and probably coffee." },
  { id: "shoestring", label: "Shoestring Startup", budget: 1500, color: "#F59E0B", weight: 2, message: "You've got grit, a charger and a dream. Good luck out there." },
  { id: "practical", label: "Practical Launch", budget: 5000, color: "#22C55E", weight: 4, message: "You've got enough to make smart moves, but not enough to waste." },
  { id: "growth", label: "Growth Ready", budget: 10000, color: "#3B82F6", weight: 2, message: "You can invest early, but every decision still counts." },
  { id: "investor", label: "Investor Mode", budget: 3000, color: "#A855F7", weight: 1, message: "A small early-investor boost. Make every dollar count — small budgets can win big with smart moves." },
];

export const SUPPLIES = [
  { id: "smartphone", name: "Smartphone Upgrade", cost: 300, tags: ["social", "video"], desc: "Improves social media, photo and short video outcomes." },
  { id: "laptop", name: "Reliable Laptop", cost: 800, tags: ["website", "email", "content"], desc: "Improves website, planning, email and content outcomes." },
  { id: "website_kit", name: "Website Starter Kit", cost: 1500, tags: ["website", "seo", "leads"], desc: "Improves website, SEO and lead conversion decisions." },
  { id: "brand_kit", name: "Brand Kit", cost: 1200, tags: ["brand", "trust"], desc: "Improves trust, design consistency and brand decisions." },
  { id: "design_software", name: "Design Software", cost: 500, tags: ["design", "social", "content"], desc: "Improves social, print and content design outcomes." },
  { id: "email_tool", name: "Email Marketing Tool", cost: 400, tags: ["email", "systems"], desc: "Protects against algorithm issues and improves repeat engagement." },
  { id: "analytics", name: "Analytics Toolkit", cost: 350, tags: ["analytics", "systems"], desc: "Improves tracking, decision-making and Month 11 performance." },
  { id: "photo_video", name: "Photo/Video Session", cost: 2000, tags: ["awareness", "trust"], desc: "Boosts awareness and trust." },
  { id: "calendar", name: "Marketing Calendar", cost: 300, tags: ["systems", "energy", "content"], desc: "Improves consistency, protects energy and supports content decisions." },
  { id: "pro_help", name: "Professional Help Voucher", cost: 3000, tags: ["website", "brand", "content", "seo", "strategy"], desc: "Improves outcomes for websites, branding, content, SEO and strategy." },
];

// Stat keys: awareness, trust, leads, energy, systems, budget
// "boost_if_has" allows supply synergy bumps.
export const SCENARIOS = [
  {
    month: 1,
    title: "Message Before Mayhem",
    focus: "Marketing strategy and positioning",
    anim: { emoji: "💭", className: "anim-float" },
    narrative: (g) => `Launch day for ${g.businessName || "your business"} is near, ${g.playerName || "trailblazer"}. Your offer is real — but the message that sells it is still fuzzy.`,
    lesson: "Clear strategy makes every future marketing decision stronger.",
    choices: [
      {
        label: "Clarify audience, offer and key message",
        cost: 0,
        outcome: "You spend a quiet morning writing a one-page strategy. Suddenly every other decision gets easier.",
        deltas: { trust: 8, systems: 10, awareness: 3, energy: -3 },
        bonus: { tag: "strategy", bonus: { systems: 4 } },
      },
      {
        label: "Launch immediately with a quick social post",
        cost: 0,
        outcome: "Your first post goes live. A few likes, but nobody is quite sure what you sell.",
        deltas: { awareness: 6, trust: -4, leads: 1, systems: -3 },
      },
      {
        label: "Spend most of the budget on a logo",
        cost: 800,
        outcome: "A shiny logo arrives. People compliment it. They still don't book.",
        deltas: { trust: 4, awareness: 2, leads: 0, systems: -2 },
      },
      {
        label: "Ask friends and family to spread the word",
        cost: 0,
        outcome: "Aunt Linda tells everyone at bingo night. One real lead, lots of goodwill.",
        deltas: { awareness: 4, trust: 2, leads: 3 },
      },
    ],
  },
  {
    month: 2,
    title: "Website Wagon Repair",
    focus: "Website design",
    anim: { emoji: "🖥", className: "anim-blink" },
    narrative: (g) => `${g.businessName || "Your business"} is getting attention from ${g.audience || "your audience"}, but they need a clear place to learn more.`,
    lesson: "Your website should answer questions and make action easy.",
    choices: [
      {
        label: "Build a simple mobile-friendly landing page",
        cost: 200,
        outcome: "Your one-page site loads fast and explains your offer. Inquiries start trickling in.",
        deltas: { leads: 10, trust: 6, systems: 4, awareness: 3 },
        bonus: { tag: "website", bonus: { leads: 6, trust: 3 } },
      },
      {
        label: "Wait until a full website is possible",
        cost: 0,
        outcome: "You wait. Months pass. People keep landing on your social with no clear next step.",
        deltas: { leads: -6, trust: -3, energy: -2 },
      },
      {
        label: "Rely only on social media",
        cost: 0,
        outcome: "Followers grow slightly. Leads do not.",
        deltas: { awareness: 4, leads: -3, systems: -3 },
      },
      {
        label: "Create a Google Business Profile first",
        cost: 0,
        outcome: "You appear on maps and get a small but steady stream of curious clicks.",
        deltas: { awareness: 6, leads: 4, trust: 3 },
        bonus: { tag: "seo", bonus: { leads: 3 } },
      },
    ],
  },
  {
    month: 3,
    title: "Social Media Fork",
    focus: "Social media",
    narrative: (g) => `${g.businessName || "Your"} page has been quiet. Crickets are sponsoring your feed.`,
    lesson: "Consistency and usefulness beat random posting.",
    choices: [
      {
        label: "Post helpful content based on customer questions",
        cost: 0,
        outcome: "Real questions become real posts. Engagement climbs steadily.",
        deltas: { awareness: 10, trust: 5, systems: 4, energy: -3 },
        bonus: { tag: "content", bonus: { awareness: 5 } },
      },
      {
        label: "Chase random trends",
        cost: 0,
        outcome: "One post pops. Nobody from it actually books anything.",
        deltas: { awareness: 7, leads: -2, trust: -3, energy: -5 },
      },
      {
        label: "Post only sales offers",
        cost: 0,
        outcome: "Engagement drops. People unfollow. The algorithm punishes you.",
        deltas: { awareness: -5, trust: -4, leads: 1 },
      },
      {
        label: "Wait until everything looks perfect",
        cost: 0,
        outcome: "Perfectionism wins the day. So does silence.",
        deltas: { awareness: -3, energy: 2, systems: -2 },
      },
    ],
  },
  {
    month: 4,
    title: "SEO River Crossing",
    focus: "SEO",
    anim: { emoji: "🔍", className: "anim-wiggle" },
    narrative: () => `Real people are searching for what you offer. They are not finding you.`,
    lesson: "SEO compounds over time and helps people find you when they are already looking.",
    choices: [
      {
        label: "Add keyword-rich website content",
        cost: 250,
        outcome: "You refresh your homepage and add a services page. Google starts paying attention.",
        deltas: { leads: 7, awareness: 6, systems: 4 },
        bonus: { tag: "seo", bonus: { leads: 5, awareness: 3 } },
      },
      {
        label: "Create a helpful blog or resource post",
        cost: 100,
        outcome: "Your post starts ranking for one long-tail keyword. Slow, steady, real.",
        deltas: { awareness: 7, leads: 5, trust: 3, energy: -3 },
        bonus: { tag: "content", bonus: { awareness: 3 } },
      },
      {
        label: "Ignore SEO and focus only on social media",
        cost: 0,
        outcome: "Your social grows a bit. Discovery stalls.",
        deltas: { awareness: 3, leads: -3 },
      },
      {
        label: "Pay for ads before fixing the website",
        cost: 500,
        outcome: "Ads send traffic to a confused page. Clicks happen. Conversions do not.",
        deltas: { awareness: 5, leads: -2, trust: -2 },
      },
    ],
  },
  {
    month: 5,
    title: "Brand Kit Trading Post",
    focus: "Branding and graphic design",
    anim: { emoji: "🎨", className: "anim-spin-slow" },
    narrative: (g) => `${g.businessName || "Your business"} looks different in every place it shows up. Followers are getting confused.`,
    lesson: "Brand consistency builds trust.",
    choices: [
      {
        label: "Create a simple brand kit",
        cost: 600,
        outcome: "Two colors, two fonts, one logo system. Suddenly everything looks intentional.",
        deltas: { trust: 12, systems: 6, awareness: 3 },
        bonus: { tag: "brand", bonus: { trust: 6 } },
      },
      {
        label: "Keep using whatever graphics are fastest",
        cost: 0,
        outcome: "Speed wins, trust loses. Customers can't tell two posts apart.",
        deltas: { trust: -6, energy: 2 },
      },
      {
        label: "Redesign everything from scratch",
        cost: 1500,
        outcome: "A pretty rebuild, but you lose a month doing it.",
        deltas: { trust: 8, energy: -8, leads: -2 },
      },
      {
        label: "Use templates with no brand direction",
        cost: 50,
        outcome: "Pretty templates, generic feel. People can't remember it was you.",
        deltas: { trust: -3, awareness: 1 },
      },
    ],
  },
  {
    month: 6,
    title: "Campaign Canyon",
    focus: "Campaign planning",
    anim: { emoji: "📣", className: "anim-shake" },
    narrative: () => `A slow season is coming. The wagon needs momentum.`,
    lesson: "Slow seasons need intentional messaging, not panic marketing.",
    choices: [
      {
        label: "Plan a seasonal campaign",
        cost: 400,
        outcome: "You map a 4-week story arc. Each post builds on the last. People are paying attention.",
        deltas: { awareness: 8, leads: 7, systems: 6, energy: -2 },
        bonus: { tag: "strategy", bonus: { leads: 4 } },
      },
      {
        label: "Create a helpful campaign around customer pain points",
        cost: 200,
        outcome: "Your free guide gets shared. Inbox grows. So does trust.",
        deltas: { trust: 8, leads: 6, awareness: 6 },
        bonus: { tag: "content", bonus: { trust: 4 } },
      },
      {
        label: "Panic and discount everything",
        cost: 0,
        outcome: "Sales tick up briefly. Margins and brand value tick down.",
        deltas: { leads: 4, trust: -6, awareness: 2 },
      },
      {
        label: "Go quiet to save energy",
        cost: 0,
        outcome: "Energy stays up. Pipeline empties.",
        deltas: { energy: 5, leads: -6, awareness: -4 },
      },
    ],
  },
  {
    month: 7,
    title: "Proof Prairie",
    focus: "Testimonials, reviews and case studies",
    anim: { emoji: "⭐", className: "anim-twinkle" },
    narrative: () => `A happy customer just sent the kindest message. The trail is providing.`,
    lesson: "Proof makes people more confident taking action.",
    choices: [
      {
        label: "Turn it into a testimonial post",
        cost: 0,
        outcome: "One post = social proof for the next month. Multiple leads mention it.",
        deltas: { trust: 8, leads: 5, awareness: 3 },
      },
      {
        label: "Ask for a Google review",
        cost: 0,
        outcome: "5-star review appears. Local searches notice.",
        deltas: { trust: 9, leads: 6 },
        bonus: { tag: "seo", bonus: { leads: 3 } },
      },
      {
        label: "Build a mini case study",
        cost: 100,
        outcome: "A one-pager with the problem, the work and the outcome. Sales loves it.",
        deltas: { trust: 10, leads: 8, systems: 4, energy: -3 },
        bonus: { tag: "strategy", bonus: { trust: 4 } },
      },
      {
        label: "Do nothing because asking feels awkward",
        cost: 0,
        outcome: "The compliment lives in your inbox forever. Nobody else sees it.",
        deltas: { trust: -2, leads: -2 },
      },
    ],
  },
  {
    month: 8,
    title: "Paid Ad Ambush",
    focus: "Digital advertising",
    anim: { emoji: "💰", className: "anim-float" },
    narrative: () => `An ad platform whispers promises of overnight growth.`,
    lesson: "Ads amplify what already exists. They do not fix unclear marketing.",
    choices: [
      {
        label: "Run a small test campaign with a clear goal",
        cost: 600,
        outcome: "You spend small, measure carefully and learn what converts.",
        deltas: { awareness: 6, leads: 6, systems: 5 },
        bonus: { tag: "analytics", bonus: { leads: 4 } },
      },
      {
        label: "Spend big without a landing page",
        cost: 1500,
        outcome: "Clicks burn through cash. Confused page = empty pipeline.",
        deltas: { awareness: 6, leads: -4, trust: -3 },
      },
      {
        label: "Boost a random post",
        cost: 100,
        outcome: "Reach goes up. Leads do not. You learn one thing: boosts are not strategy.",
        deltas: { awareness: 4, leads: 0 },
      },
      {
        label: "Wait until the message and website are stronger",
        cost: 0,
        outcome: "You skip the cash burn. Next quarter, ads will work harder.",
        deltas: { systems: 4, energy: 3, trust: 2 },
      },
    ],
  },
  {
    month: 9,
    title: "Community Campfire",
    focus: "Networking, partnerships and local visibility",
    anim: { emoji: "🔥", className: "anim-flicker" },
    narrative: () => `A local event/partnership opportunity just opened up around the campfire.`,
    lesson: "Relationships matter, but follow-up turns them into opportunity.",
    choices: [
      {
        label: "Attend with a clear pitch and follow-up plan",
        cost: 150,
        outcome: "You leave with three warm intros. Two convert within a month.",
        deltas: { leads: 10, trust: 6, awareness: 5, energy: -3 },
        bonus: { tag: "systems", bonus: { leads: 5 } },
      },
      {
        label: "Attend casually with no materials",
        cost: 50,
        outcome: "Nice chats. No business cards. Vague memories.",
        deltas: { awareness: 4, leads: 1, energy: -3 },
      },
      {
        label: "Skip it because things are busy",
        cost: 0,
        outcome: "You catch up on email. Someone else gets the partnership.",
        deltas: { energy: 4, leads: -5, awareness: -3 },
      },
      {
        label: "Sponsor it without knowing the goal",
        cost: 1200,
        outcome: "Your logo on a banner. Nobody asks who you are.",
        deltas: { awareness: 6, trust: 2, leads: -1 },
      },
    ],
  },
  {
    month: 10,
    title: "Content Burnout Blizzard",
    focus: "Content creation and content systems",
    anim: { emoji: "😴", className: "anim-pulse-slow" },
    narrative: (g) => `${g.playerName || "You"} are tired. The content treadmill has won this round.`,
    lesson: "Marketing systems prevent burnout.",
    choices: [
      {
        label: "Batch content for the next month",
        cost: 100,
        outcome: "One afternoon of focused work = 30 days of posts queued.",
        deltas: { energy: 6, systems: 8, awareness: 4 },
        bonus: { tag: "systems", bonus: { energy: 4 } },
      },
      {
        label: "Repurpose old content",
        cost: 0,
        outcome: "Old gold gets new life. Audience barely notices, and engagement holds.",
        deltas: { energy: 4, awareness: 3, systems: 4 },
        bonus: { tag: "content", bonus: { awareness: 3 } },
      },
      {
        label: "Hire help or delegate",
        cost: 600,
        outcome: "You buy back time. Output improves, energy returns.",
        deltas: { energy: 10, systems: 6, awareness: 4 },
      },
      {
        label: "Stop posting completely",
        cost: 0,
        outcome: "Silence. The algorithm forgets you exist.",
        deltas: { awareness: -8, energy: 6, leads: -4 },
      },
    ],
  },
  {
    month: 11,
    title: "Analytics Outpost",
    focus: "Analytics and reporting",
    anim: { emoji: "📊", className: "anim-bounce" },
    narrative: () => `You've tried a lot of things. Do you know what actually worked?`,
    lesson: "You cannot improve what you do not track.",
    choices: [
      {
        label: "Review website, social and lead data",
        cost: 0,
        outcome: "You find the two channels that produce 80% of your leads. Game-changing.",
        deltas: { systems: 12, leads: 5, trust: 3 },
        bonus: { tag: "analytics", bonus: { systems: 6, leads: 3 } },
      },
      {
        label: "Ask customers how they found the business",
        cost: 0,
        outcome: "A surprising answer emerges: word of mouth and Google reviews are doing the heavy lifting.",
        deltas: { systems: 6, leads: 4, trust: 3 },
      },
      {
        label: "Keep guessing",
        cost: 0,
        outcome: "You keep making the same investments. The same results follow.",
        deltas: { systems: -6, energy: 2 },
      },
      {
        label: "Change everything at once",
        cost: 200,
        outcome: "Chaos. You can't tell what worked because nothing stayed the same.",
        deltas: { systems: -4, energy: -4, leads: -2 },
      },
    ],
  },
  {
    month: 12,
    title: "Year Two Trailhead",
    focus: "Marketing roadmap",
    anim: { emoji: "🗺", className: "anim-float" },
    narrative: () => `The first year is closing. Year two needs a plan, not just a hope.`,
    lesson: "Growth becomes easier when marketing moves from random activity to a repeatable system.",
    choices: [
      {
        label: "Build a 90-day marketing roadmap",
        cost: 200,
        outcome: "You write down the next 90 days: one big goal, three campaigns, weekly rhythm.",
        deltas: { systems: 12, leads: 5, energy: 4, trust: 3 },
        bonus: { tag: "strategy", bonus: { systems: 6 } },
      },
      {
        label: "Keep reacting month-to-month",
        cost: 0,
        outcome: "You stay flexible. You also stay tired. Year two starts shaky.",
        deltas: { energy: 2, systems: -6, leads: -3 },
      },
      {
        label: "Invest based on the weakest stat",
        cost: 300,
        outcome: "You aim your next quarter at your weakest area. Smart trail leadership.",
        deltas: { systems: 8, trust: 4, leads: 4 },
      },
      {
        label: "Quit marketing because referrals are enough",
        cost: 0,
        outcome: "Referrals are great until they aren't. You're banking on hope.",
        deltas: { awareness: -8, leads: -6, systems: -6 },
      },
    ],
  },
];

export const RANDOM_EVENTS = [
  {
    id: "algorithm_stampede",
    title: "Algorithm Stampede",
    message: "The algorithm stampeded through camp and your reach took a tumble.",
    apply: (stats, supplies) => {
      const has = supplies.some((s) => ["email_tool", "website_kit", "pro_help"].includes(s));
      return has ? { awareness: -3 } : { awareness: -10 };
    },
  },
  {
    id: "review_gold_rush",
    title: "Google Review Gold Rush",
    message: "A happy customer left a glowing review. The trail provides.",
    apply: () => ({ trust: 6, leads: 5 }),
  },
  {
    id: "wagon_wheel_breaks",
    title: "Website Wagon Wheel Breaks",
    message: "Your mobile site loaded slower than a wagon in mud.",
    apply: (stats, supplies) => {
      const has = supplies.some((s) => ["website_kit", "pro_help", "laptop"].includes(s));
      return has ? { leads: -3 } : { leads: -8 };
    },
  },
  {
    id: "budget_bandits",
    title: "Budget Bandits",
    message: "Unexpected expenses raided the supply wagon.",
    apply: () => ({ budget: -400 }),
  },
  {
    id: "canva_tornado",
    title: "Canva Tornado",
    message: "Eleven graphics. Eleven fonts. One confused audience.",
    apply: (stats, supplies) => {
      const has = supplies.some((s) => ["brand_kit", "design_software", "pro_help"].includes(s));
      return has ? { trust: -2 } : { trust: -7 };
    },
  },
  {
    id: "chamber_campfire",
    title: "Chamber Campfire",
    message: "A networking opportunity popped up around the campfire.",
    apply: (stats) => (stats.trust >= 55 || stats.leads >= 55 ? { leads: 7 } : { leads: 3 }),
  },
  {
    id: "inbox_oasis",
    title: "Inbox Oasis",
    message: "An email subscriber turned into a warm lead.",
    apply: (stats, supplies) => (supplies.includes("email_tool") ? { leads: 8 } : { leads: 2 }),
  },
  {
    id: "viral_mirage",
    title: "Viral Mirage",
    message: "One post got attention, but the leads vanished like a desert mirage.",
    apply: (stats, supplies) => {
      const websiteReady = supplies.some((s) => ["website_kit", "pro_help"].includes(s));
      return websiteReady ? { awareness: 8, leads: 4 } : { awareness: 8, leads: -2 };
    },
  },
  {
    id: "printer_jam",
    title: "Printer Jam Goblin",
    message: "A last-minute flyer had a typo the size of a tumbleweed.",
    apply: (stats, supplies) => {
      const has = supplies.some((s) => ["brand_kit", "pro_help", "calendar"].includes(s));
      return has ? { trust: -2 } : { trust: -6 };
    },
  },
  {
    id: "customer_question_jackpot",
    title: "Customer Question Jackpot",
    message: "A customer asked a great question and accidentally handed you content gold.",
    apply: (stats, supplies) => {
      const has = supplies.some((s) => ["calendar", "design_software"].includes(s));
      return has ? { awareness: 8, trust: 4 } : { awareness: 4, trust: 2 };
    },
  },
];

export const INITIAL_STATS = {
  awareness: 25,
  trust: 25,
  leads: 20,
  energy: 70,
  systems: 25,
  budget: 0,
};

// Picks weighted wheel outcome
export function rollWheel() {
  const pool = [];
  WHEEL_OUTCOMES.forEach((o) => {
    for (let i = 0; i < o.weight; i++) pool.push(o);
  });
  return pool[Math.floor(Math.random() * pool.length)];
}

/**
 * Build a session-specific scenario order for replayability.
 * - Month 1 ("Message Before Mayhem") stays as the intro
 * - Month 12 ("Year Two Trailhead") stays as the closer
 * - Months 2–11 are Fisher-Yates shuffled
 * Returns an array of 12 scenario objects in display order; consumers index by
 * `state.month - 1`. The original `scenario.month` numbers are NOT relied on
 * for ordering anywhere — display uses `state.month` directly.
 */
export function buildSessionScenarios() {
  const first = SCENARIOS[0];
  const last = SCENARIOS[SCENARIOS.length - 1];
  const middle = SCENARIOS.slice(1, SCENARIOS.length - 1);
  for (let i = middle.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [middle[i], middle[j]] = [middle[j], middle[i]];
  }
  return [first, ...middle, last];
}

// Apply supply synergy: if any supply tag matches choice bonus.tag, add bonus deltas
export function applySupplyBonus(choice, supplies) {
  if (!choice.bonus) return {};
  const bonusTag = choice.bonus.tag;
  const tagsOwned = new Set();
  supplies.forEach((sid) => {
    const item = SUPPLIES.find((s) => s.id === sid);
    if (item) item.tags.forEach((t) => tagsOwned.add(t));
  });
  return tagsOwned.has(bonusTag) ? choice.bonus.bonus : {};
}

// Pick a random event, weighted slightly toward negative for replayability
export function pickRandomEvent() {
  return RANDOM_EVENTS[Math.floor(Math.random() * RANDOM_EVENTS.length)];
}

